/*
 * INKora — DTF Transfers By Size builder (stage A).
 *
 * Customers upload up to 25 designs, pick one or more sizes per design, see a live price (variant lookup + cumulative
 * volume discount) and add everything to the cart in one go. Each design size becomes a normal cart line with a real
 * quantity; the file travels as the hidden line-item property "_Artwork File" (Shopify native upload, 20 MB per file),
 * which the existing cart artwork card already knows how to show.
 *
 * Pricing facts live in the product's variants:
 *   "10-19 sq in"  -> area bucket (ceil(area) falls inside the range); "9 sq in" is a bucket of exactly 9
 *   `4" x 4"`      -> popular size (exact box match, either orientation)
 * The volume discount shown here must match the Shopify automatic discounts created in the admin (same tiers).
 */
(function () {
  'use strict';

  // ---------- pure pricing helpers (also used by the node test) ----------
  function parseVariants(list) {
    var buckets = [];
    var popular = [];
    (list || []).forEach(function (v) {
      var t = String(v.title || '').trim();
      var m = t.match(/^(\d+)(?:\s*-\s*(\d+))?\s*sq in$/i);
      if (m) {
        buckets.push({ id: v.id, title: t, price: v.price, min: +m[1], max: m[2] ? +m[2] : +m[1] });
        return;
      }
      m = t.match(/^(\d+(?:\.\d+)?)"\s*x\s*(\d+(?:\.\d+)?)"$/i);
      if (m) popular.push({ id: v.id, title: t, price: v.price, w: +m[1], h: +m[2] });
    });
    buckets.sort(function (a, b) { return a.min - b.min; });
    return { buckets: buckets, popular: popular };
  }

  function round2(n) { return Math.round(n * 100) / 100; }

  // Returns { variant, kind: 'popular'|'bucket' } or null when the size is outside the catalog.
  function findVariant(parsed, w, h) {
    w = round2(w); h = round2(h);
    if (!(w > 0) || !(h > 0)) return null;
    for (var i = 0; i < parsed.popular.length; i++) {
      var p = parsed.popular[i];
      if ((p.w === w && p.h === h) || (p.w === h && p.h === w)) return { variant: p, kind: 'popular' };
    }
    var area = Math.ceil(round2(w * h) - 1e-9);
    if (area < 1) area = 1;
    for (var j = 0; j < parsed.buckets.length; j++) {
      var b = parsed.buckets[j];
      if (area >= b.min && area <= b.max) return { variant: b, kind: 'bucket' };
    }
    return null;
  }

  function tierFor(tiers, total) {
    var cur = { min: 1, off: 0 };
    (tiers || []).forEach(function (t) { if (total >= t.min) cur = t; });
    return cur;
  }

  function nextTier(tiers, total) {
    for (var i = 0; i < (tiers || []).length; i++) if (total < tiers[i].min) return tiers[i];
    return null;
  }

  // Shopify applies percentage discounts per line and rounds each unit price to cents.
  function unitAfterDiscount(priceCents, off) {
    return Math.round(priceCents * (100 - off) / 100);
  }

  var api = {
    parseVariants: parseVariants,
    findVariant: findVariant,
    tierFor: tierFor,
    nextTier: nextTier,
    unitAfterDiscount: unitAfterDiscount,
    round2: round2,
  };
  if (typeof window !== 'undefined') window.InkoraDtfSizePricing = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  // ---------- DOM builder ----------
  if (typeof document === 'undefined') return;
  var root = document.querySelector('[data-dtfs-root]');
  if (!root) return;

  var cfgEl = root.querySelector('[data-dtfs-config]');
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var parsed = parseVariants(cfg.variants);
  var tiers = cfg.tiers || [];
  var MAX_FILES = cfg.maxFiles || 25;
  var MAX_BYTES = (cfg.maxFileMb || 20) * 1024 * 1024;
  var MAX_SIDE_ONE = cfg.maxWidthIn || 13; // the printer is 13 in wide: the shorter side of a design must fit
  var RASTER = /^image\/(png|jpe?g|webp|gif)$/i;

  var els = {
    drop: root.querySelector('[data-dtfs-drop]'),
    input: root.querySelector('[data-dtfs-input]'),
    list: root.querySelector('[data-dtfs-list]'),
    empty: root.querySelector('[data-dtfs-empty]'),
    steps: root.querySelector('[data-dtfs-steps]'),
    tiers: root.querySelector('[data-dtfs-tiers]'),
    nudge: root.querySelector('[data-dtfs-nudge]'),
    pieces: root.querySelector('[data-dtfs-pieces]'),
    sub: root.querySelector('[data-dtfs-subtotal]'),
    save: root.querySelector('[data-dtfs-savings]'),
    saveRow: root.querySelector('[data-dtfs-savings-row]'),
    precutRow: root.querySelector('[data-dtfs-precut-row]'),
    precutToggle: root.querySelector('[data-dtfs-precut]'),
    precutCost: root.querySelector('[data-dtfs-precut-cost]'),
    total: root.querySelector('[data-dtfs-total]'),
    add: root.querySelector('[data-dtfs-add]'),
    error: root.querySelector('[data-dtfs-error]'),
    swatches: root.querySelector('[data-dtfs-swatches]'),
    custom: root.querySelector('[data-dtfs-custom]'),
    cust: root.querySelector('[data-dtfs-cust]'),
  };

  var designs = [];
  var seq = 0;
  var activeRow = null;
  var activeId = null; // design shown in the "Customize your image" preview
  var previewBg = 'checker';

  var POPULAR_LABELS = {
    '2x2': 'Tag print / neck label',
    '3x3': 'Small tag or sleeve icon',
    '4x2': 'Name label / small chest text',
    '4x4': 'Left-chest logo',
    '5x3': 'Small badge logo',
    '5x5': 'Small chest / youth print',
    '6x6': 'Standard left chest / youth',
    '7x7': 'Small front (adult)',
    '8x8': 'Medium front print',
    '9x9': 'Large chest / medium front',
    '9x11': 'Vertical front / youth',
    '10x10': 'Standard adult front',
    '11x5': 'Chest banner / sleeve',
    '11x11': 'Large adult front',
    '11x14': 'Oversized front',
    '12x17': 'Full front / jersey',
  };

  function money(cents) {
    var v = (cents / 100).toFixed(2);
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function popularKey(p) { return p.w + 'x' + p.h; }

  function totalPieces() {
    var n = 0;
    designs.forEach(function (d) { d.rows.forEach(function (r) { n += r.qty; }); });
    return n;
  }

  // Smart sizing: print at 300 dpi when it fits, otherwise cap the long side so the shorter side stays within the printer.
  function smartSize(d) {
    if (!d.nat) return { w: 4, h: 4 };
    var w = d.nat.w / 300;
    var h = d.nat.h / 300;
    var long = Math.max(w, h);
    if (long > 12) { var k = 12 / long; w *= k; h *= k; }
    if (long < 2) { var k2 = 2 / long; w *= k2; h *= k2; }
    if (Math.min(w, h) > 12) { var k3 = 12 / Math.min(w, h); w *= k3; h *= k3; }
    return { w: round2(Math.round(w * 4) / 4), h: round2(Math.round(h * 4) / 4) };
  }

  function newRow(d) {
    var s = smartSize(d);
    return { id: ++seq, mode: 'smart', w: s.w, h: s.h, qty: 1, lock: true };
  }

  function rowResult(d, r) {
    // The box that is priced.
    var w = r.w, h = r.h;
    if (r.mode === 'popular' && r.pop) { w = r.pop.w; h = r.pop.h; }
    var found = findVariant(parsed, w, h);
    var tooWide = Math.min(w, h) > MAX_SIDE_ONE + 1e-9;
    return { w: w, h: h, found: found, tooWide: tooWide };
  }

  function dpiOf(d, r) {
    if (!d.nat) return null;
    var w = r.w, h = r.h;
    if (r.mode === 'popular' && r.pop) {
      // design is fitted inside the popular box
      var scale = Math.min(r.pop.w / (d.nat.w / 300), r.pop.h / (d.nat.h / 300));
      w = (d.nat.w / 300) * scale; h = (d.nat.h / 300) * scale;
    }
    return Math.round(Math.min(d.nat.w / w, d.nat.h / h));
  }

  // Biggest size (in) at which the image still prints at 300 dpi; null if that would be smaller than the minimum.
  function maxSizeAt300(d) {
    if (!d.nat || !d.nat.w || !d.nat.h || d.nat.vector) return null;
    var w = Math.floor(d.nat.w / 300 * 100) / 100;
    var h = Math.floor(d.nat.h / 300 * 100) / 100;
    if (Math.min(w, h) < 0.5) return null;
    return { w: w, h: h };
  }

  // ---------- file handling ----------
  function readNatural(file) {
    return new Promise(function (resolve) {
      if (!RASTER.test(file.type) && file.type !== 'image/svg+xml') return resolve(null);
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        var w = img.naturalWidth, h = img.naturalHeight;
        if (file.type === 'image/svg+xml' || !w || !h) { w = w || 1200; h = h || 1200; }
        resolve({ url: url, w: w, h: h, vector: file.type === 'image/svg+xml' });
      };
      img.onerror = function () { resolve({ url: url, w: 0, h: 0, bad: true }); };
      img.src = url;
    });
  }

  function addFiles(fileList) {
    var files = Array.prototype.slice.call(fileList || []);
    if (!files.length) return;
    var room = MAX_FILES - designs.length;
    if (room <= 0) { setError('You can upload up to ' + MAX_FILES + ' designs at a time.'); return; }
    if (files.length > room) { setError('Only the first ' + room + ' files were added (limit ' + MAX_FILES + ').'); files = files.slice(0, room); } else { setError(''); }
    files.forEach(function (file) { addDesign(file, null); });
    render();
  }

  function addDesign(file, studioState) {
    var d = { id: ++seq, file: file, name: file.name, nat: null, url: '', notes: '', link: '', rows: [], big: file.size > MAX_BYTES, studio: studioState || null, edited: !!studioState };
    designs.push(d);
    activeId = d.id;
    readNatural(file).then(function (n) {
      if (n && !n.bad) { d.nat = { w: n.w, h: n.h, vector: n.vector }; d.url = n.url; }
      d.rows = [newRow(d)];
      render();
    });
    return d;
  }

  // ---------- design studio (stage B) ----------
  function openStudio(opts) {
    var go = function () {
      opts.cssHref = cfg.studioCss; opts.maxBytes = MAX_BYTES;
      window.InkoraDtfStudio.open(opts);
    };
    if (window.InkoraDtfStudio) return go();
    if (!cfg.studioJs) return;
    var s = document.createElement('script');
    s.src = cfg.studioJs; s.async = true; s.onload = go;
    s.onerror = function () { setError('The editor could not be loaded. Please try again.'); };
    document.head.appendChild(s);
  }

  function editDesign(d) {
    openStudio({
      file: d.file, name: d.name, state: d.studio,
      onSave: function (file, state) {
        if (d.url) URL.revokeObjectURL(d.url);
        d.file = file; d.name = file.name; d.big = file.size > MAX_BYTES; d.studio = state; d.edited = true; d.url = '';
        d.origFile = null; d.origName = ''; d.bgOn = false; d.noBg = false;
        readNatural(file).then(function (n) {
          if (n && !n.bad) { d.nat = { w: n.w, h: n.h, vector: false }; d.url = n.url; }
          d.rows.forEach(function (r) {
            if (r.mode === 'smart') { var s = smartSize(d); r.w = s.w; r.h = s.h; }
            else if (r.mode === 'custom' && r.lock && d.nat) r.h = Math.max(0.5, round2(r.w / (d.nat.w / d.nat.h)));
          });
          render();
        });
        render();
      },
    });
  }

  function createDesign() {
    if (designs.length >= MAX_FILES) { setError('You can add up to ' + MAX_FILES + ' designs at a time.'); return; }
    setError('');
    openStudio({
      onSave: function (file, state) { addDesign(file, state); render(); },
    });
  }

  // ---------- "Customize your image": big preview, measures, remove background ----------
  var MAX_CUT_PX = 36e6; // 6000 x 6000: bigger images are not edited in the browser

  function fmtIn(n) { return String(round2(n)).replace(/(\.\d)0$/, '$1') + '"'; }

  function currentDesign() {
    var d = designs.filter(function (x) { return x.id === activeId; })[0];
    if (!d) { d = designs[0] || null; activeId = d ? d.id : null; }
    return d;
  }

  // The measures drawn on the preview: the size of the design as it will be printed.
  function shownDims(d, r) {
    var res = rowResult(d, r);
    var w = res.w, h = res.h;
    if (r.mode === 'popular' && r.pop && d.nat && d.nat.w && d.nat.h) {
      var k = Math.min(r.pop.w / d.nat.w, r.pop.h / d.nat.h);
      w = d.nat.w * k; h = d.nat.h * k;
    }
    return { w: w, h: h };
  }

  function swapFile(d, file, name) {
    if (d.url) URL.revokeObjectURL(d.url);
    d.file = file; d.name = name || file.name; d.big = file.size > MAX_BYTES; d.url = URL.createObjectURL(file);
  }

  // Makes the solid background that touches the edges transparent (flood fill from the border).
  function cutFile(file, tol) {
    return new Promise(function (resolve) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!w || !h || w * h > MAX_CUT_PX) { URL.revokeObjectURL(url); return resolve({ error: 'big' }); }
          var c = document.createElement('canvas');
          c.width = w; c.height = h;
          var ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          var id = ctx.getImageData(0, 0, w, h);
          var px = id.data;
          // Background colour = the colour that appears most along the whole border of the image
          // (robust when something touches a corner, e.g. a hand at the bottom edge).
          var border = [];
          var stepX = Math.max(1, Math.floor(w / 600)), stepY = Math.max(1, Math.floor(h / 600));
          var bx, by;
          for (bx = 0; bx < w; bx += stepX) { border.push(bx); border.push((h - 1) * w + bx); }
          for (by = 0; by < h; by += stepY) { border.push(by * w); border.push(by * w + (w - 1)); }
          var hist = {}, clear = 0;
          border.forEach(function (k) {
            var i = k * 4;
            if (px[i + 3] < 200) { clear++; return; }
            var key = (px[i] >> 4) + ',' + (px[i + 1] >> 4) + ',' + (px[i + 2] >> 4);
            var b = hist[key] || (hist[key] = { n: 0, r: 0, g: 0, b: 0 });
            b.n++; b.r += px[i]; b.g += px[i + 1]; b.b += px[i + 2];
          });
          if (clear > border.length * 0.6) return resolve({ error: 'transparent' });
          var top = null;
          Object.keys(hist).forEach(function (key) { if (!top || hist[key].n > top.n) top = hist[key]; });
          if (!top) return resolve({ error: 'nothing' });
          var br = top.r / top.n, bgc = top.g / top.n, bb = top.b / top.n;
          function isBg(i) {
            if (px[i + 3] < 12) return true;
            return Math.max(Math.abs(px[i] - br), Math.abs(px[i + 1] - bgc), Math.abs(px[i + 2] - bb)) <= tol;
          }
          var seen = new Uint8Array(w * h);
          var stack = [];
          function push(x, y) {
            var k = y * w + x;
            if (seen[k]) return;
            if (!isBg(k * 4)) return;
            seen[k] = 1; stack.push(k);
          }
          var x, y;
          for (x = 0; x < w; x++) { push(x, 0); push(x, h - 1); }
          for (y = 0; y < h; y++) { push(0, y); push(w - 1, y); }
          while (stack.length) {
            var k = stack.pop();
            var cx = k % w, cy = (k - cx) / w;
            if (cx > 0) push(cx - 1, cy);
            if (cx < w - 1) push(cx + 1, cy);
            if (cy > 0) push(cx, cy - 1);
            if (cy < h - 1) push(cx, cy + 1);
          }
          var removed = 0;
          for (var n = 0; n < seen.length; n++) {
            if (seen[n]) { px[n * 4 + 3] = 0; removed++; }
          }
          // soften the 1px fringe next to the removed area
          for (var m = 0; m < seen.length; m++) {
            if (seen[m]) continue;
            var mx = m % w, my = (m - mx) / w;
            var near = (mx > 0 && seen[m - 1]) || (mx < w - 1 && seen[m + 1]) || (my > 0 && seen[m - w]) || (my < h - 1 && seen[m + w]);
            if (near) px[m * 4 + 3] = Math.min(px[m * 4 + 3], 150);
          }
          if (removed < w * h * 0.01) return resolve({ error: 'nothing' });
          ctx.putImageData(id, 0, 0);
          c.toBlob(function (blob) {
            if (!blob) return resolve({ error: 'fail' });
            var base = String(file.name || 'design').replace(/\.[^.]+$/, '').replace(/-nobg$/, '');
            resolve({ file: new File([blob], base + '-nobg.png', { type: 'image/png' }) });
          }, 'image/png');
        } catch (e) { resolve({ error: 'fail' }); }
      };
      img.onerror = function () { URL.revokeObjectURL(url); resolve({ error: 'fail' }); };
      img.src = url;
    });
  }

  function applyCut(d, on) {
    if (d.busy) return;
    if (!on) {
      if (d.origFile) { swapFile(d, d.origFile, d.origName); d.origFile = null; d.origName = ''; }
      d.bgOn = false; d.cutMsg = '';
      render();
      return;
    }
    if (!d.origFile) { d.origFile = d.file; d.origName = d.name; }
    d.busy = true; d.cutMsg = '';
    renderCustom();
    cutFile(d.origFile, d.tol || 40).then(function (res) {
      d.busy = false;
      if (res.file) {
        swapFile(d, res.file);
        d.bgOn = true; d.noBg = false; d.cutMsg = '';
      } else {
        d.bgOn = false;
        d.origFile = null; d.origName = '';
        if (res.error === 'transparent') { d.noBg = true; d.cutMsg = 'This image already has a transparent background.'; }
        else if (res.error === 'nothing') d.cutMsg = 'We could not find a solid background to remove. Try Edit design for a detailed cut.';
        else if (res.error === 'big') d.cutMsg = 'This image is too large to edit here. Use Edit design instead.';
        else d.cutMsg = 'Could not remove the background. Please try again.';
      }
      render();
    });
  }

  function renderCustom() {
    if (!els.custom || !els.cust) return;
    var d = currentDesign();
    els.custom.hidden = !d;
    if (!d) { els.cust.innerHTML = ''; return; }
    var r = (activeRow && d.rows.indexOf(activeRow) >= 0) ? activeRow : d.rows[0];
    var dims = (r && d.nat && d.nat.w) ? shownDims(d, r) : null;
    var raster = !!(d.nat && !d.nat.vector && !d.nat.bad && RASTER.test(d.file.type || '') || d.bgOn);
    var canEdit = !!(d.url && d.nat && !d.nat.bad && cfg.studioJs);

    var thumbs = '';
    designs.forEach(function (x, i) {
      thumbs += '<button type="button" class="dtfs-rail__thumb dtfs-bg--' + previewBg + (x.id === d.id ? ' is-active' : '') + '" data-pick="' + x.id + '" aria-label="Show design ' + (i + 1) + '">' +
        (x.url ? '<img src="' + x.url + '" alt="">' : '<span class="dtfs-design__file">' + esc((x.name.split('.').pop() || 'file').toUpperCase()) + '</span>') +
        '</button><span class="dtfs-rail__cap">Design ' + (i + 1) + '</span>';
    });

    var cut = '<div class="dtfs-rail__box">' +
      '<label class="dtfs-switch' + ((raster && !d.noBg) ? '' : ' is-off') + '"><span>Remove background</span>' +
      '<input type="checkbox" data-cut' + (d.bgOn ? ' checked' : '') + ((raster && !d.noBg && !d.busy) ? '' : ' disabled') + '><i></i></label>';
    if (d.bgOn) cut += '<label class="dtfs-tol">Strength<input type="range" min="10" max="90" step="5" value="' + (d.tol || 40) + '" data-tol></label>';
    if (d.busy) cut += '<p class="dtfs-rail__hint">Removing background&hellip;</p>';
    else if (d.cutMsg) cut += '<p class="dtfs-rail__hint">' + esc(d.cutMsg) + '</p>';
    else if (!raster) cut += '<p class="dtfs-rail__hint">Available for PNG, JPG and WEBP images.</p>';
    else cut += '<p class="dtfs-rail__hint">Works best on a solid color background.</p>';
    cut += '</div>';

    var stage;
    if (d.url) {
      var guides = '';
      if (dims) {
        guides = '<span class="dtfs-guide dtfs-guide--w"><i></i><b>' + fmtIn(dims.w) + '</b><i></i></span>' +
          '<span class="dtfs-guide dtfs-guide--h"><i></i><b>' + fmtIn(dims.h) + '</b><i></i></span>';
      }
      stage = '<div class="dtfs-stage dtfs-bg--' + previewBg + '">' +
        '<button type="button" class="dtfs-stage__btn dtfs-stage__del" data-del aria-label="Remove this design" title="Remove this design">&times;</button>' +
        '<button type="button" class="dtfs-stage__btn dtfs-stage__zoom" data-zoom aria-label="Zoom" title="Zoom">&#9906;</button>' +
        '<div class="dtfs-stage__wrap">' + guides + '<img src="' + d.url + '" alt="' + esc(d.name) + '"></div>' +
        '</div>';
    } else {
      stage = '<div class="dtfs-stage dtfs-stage--none"><p>Preview not available for this file type. Choose your size below.</p></div>';
    }

    var tools = '<div class="dtfs-tools">' +
      (canEdit ? '<button type="button" class="dtfs-tool dtfs-tool--main" data-edit>&#9998; Edit design</button>' +
        '<button type="button" class="dtfs-tool" data-addtext><b>Tt</b> Add text</button>' : '') +
      '<span class="dtfs-tools__sp"></span>' +
      '<button type="button" class="dtfs-tool" data-new>Design Studio</button>' +
      '</div>';
    var left = Math.max(0, MAX_FILES - designs.length);
    var leftTxt = left === 0 ? 'Limit reached' : left + ' more design' + (left === 1 ? '' : 's') + ' allowed';
    var more = '<div class="dtfs-upwrap"><button type="button" class="dtfs-upmore" data-more' + (left === 0 ? ' disabled' : '') + '>' +
      '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V4M12 4L7.5 8.5M12 4l4.5 4.5"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>' +
      '<span>Upload more designs</span></button>' +
      '<p class="dtfs-upcount"><span>' + esc(leftTxt) + '</span><span>' + designs.length + ' of ' + MAX_FILES + ' uploaded</span></p></div>';

    els.cust.innerHTML = '<div class="dtfs-rail">' + cut + '<div class="dtfs-rail__thumbs">' + thumbs + '</div></div>' +
      '<div class="dtfs-main">' + stage + tools + more + '</div>';
  }

  if (els.cust) {
    els.cust.addEventListener('click', function (e) {
      var d = currentDesign();
      if (!d) return;
      var t = e.target.closest('[data-pick],[data-del],[data-zoom],[data-edit],[data-addtext],[data-more],[data-new]');
      if (!t) return;
      if (t.hasAttribute('data-more')) { els.input.click(); return; }
      if (t.hasAttribute('data-new')) { createDesign(); return; }
      if (t.hasAttribute('data-pick')) { activeId = parseInt(t.getAttribute('data-pick'), 10); render(); return; }
      if (t.hasAttribute('data-zoom')) { openZoom(d); return; }
      if (t.hasAttribute('data-del')) {
        if (d.url) URL.revokeObjectURL(d.url);
        designs = designs.filter(function (x) { return x !== d; });
        setError(''); render(); return;
      }
      if (t.hasAttribute('data-edit')) { editDesign(d); return; }
      if (t.hasAttribute('data-addtext')) {
        editDesign(d);
        // once the editor is on screen, press its "Add text" button
        var tries = 0;
        var iv = setInterval(function () {
          var b = document.querySelector('[data-act="add-text"]');
          if (b && b.offsetParent) { clearInterval(iv); setTimeout(function () { b.click(); }, 700); }
          else if (++tries > 40) clearInterval(iv);
        }, 250);
      }
    });
    els.cust.addEventListener('change', function (e) {
      var d = currentDesign();
      if (!d) return;
      if (e.target.matches('[data-cut]')) applyCut(d, e.target.checked);
      else if (e.target.matches('[data-tol]')) {
        d.tol = parseInt(e.target.value, 10) || 40;
        if (d.bgOn) { var keep = d.origFile; d.bgOn = false; d.origFile = keep; applyCut(d, true); }
      }
    });
  }

  // ---------- popular size menu with illustrations ----------
  // A t-shirt (adult chest ~20 in wide) with the print drawn at its real scale and typical position.
  // Small icons in the rows; hovering a row shows a big shirt with the measures written on it.
  var TEE_UNITS_PER_IN = 2.6;
  function sizeIcon(p, dims) {
    var u = TEE_UNITS_PER_IN;
    var w = Math.min(p.w * u, 50), h = Math.min(p.h * u, 62);
    var key = p.w + 'x' + p.h;
    var cx = 50, top = dims ? 32 : 27;
    if (key === '2x2') { top = dims ? 24 : 17; }                       // neck label
    else if (key === '3x3') { cx = dims ? 19 : 17; top = dims ? 32 : 28; } // sleeve icon
    else if (key === '4x2' || key === '4x4' || key === '5x3' || key === '6x6') { cx = 64; top = dims ? 32 : 28; } // left chest
    var x = cx - w / 2, y = top;
    var shirt = 'M31 12 L15 21 L5 38 L18 45 L24 38 L24 92 L76 92 L76 38 L82 45 L95 38 L85 21 L69 12 C63 19 37 19 31 12 Z';
    var s = '<svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">' +
      '<path d="' + shirt + '" fill="#eef0f4" stroke="#c9ced8" stroke-width="1.6" stroke-linejoin="round"/>' +
      '<rect x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + w.toFixed(1) + '" height="' + h.toFixed(1) + '" rx="1.2" fill="rgba(228,0,122,.26)" stroke="#e4007a" stroke-width="1.4"/>';
    if (dims) {
      var c = '#1a6fd1', ly = y - 3.6, lx = x - 3.6;
      var ln = function (x1, y1, x2, y2) { return '<line x1="' + x1.toFixed(1) + '" y1="' + y1.toFixed(1) + '" x2="' + x2.toFixed(1) + '" y2="' + y2.toFixed(1) + '" stroke="' + c + '" stroke-width="0.7"/>'; };
      s += ln(x, ly, x + w, ly) + ln(x, ly - 1.5, x, ly + 1.5) + ln(x + w, ly - 1.5, x + w, ly + 1.5);
      s += '<text x="' + cx + '" y="' + (ly - 1.8).toFixed(1) + '" text-anchor="middle" font-size="6.2" font-weight="700" fill="' + c + '" font-family="sans-serif">' + p.w + '"</text>';
      s += ln(lx, y, lx, y + h) + ln(lx - 1.5, y, lx + 1.5, y) + ln(lx - 1.5, y + h, lx + 1.5, y + h);
      s += '<text transform="translate(' + (lx - 2).toFixed(1) + ' ' + (y + h / 2).toFixed(1) + ') rotate(-90)" text-anchor="middle" font-size="6.2" font-weight="700" fill="' + c + '" font-family="sans-serif">' + p.h + '"</text>';
    }
    return s + '</svg>';
  }

  var openMenu = null;
  document.addEventListener('click', function (e) {
    if (openMenu && !openMenu.contains(e.target)) closeMenu();
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && openMenu) closeMenu(); });
  function closeMenu() {
    if (!openMenu) return;
    var pn = openMenu.querySelector('.dtfs-dd__panel'), b = openMenu.querySelector('.dtfs-dd__btn');
    if (pn) pn.hidden = true;
    if (b) b.setAttribute('aria-expanded', 'false');
    openMenu = null;
  }

  // returns the DOM node of the custom dropdown; onPick(p) is called with the chosen popular variant
  function popularMenu(current, onPick) {
    var wrap = el('div', 'dtfs-dd');
    var sorted = parsed.popular.slice().sort(function (a, b) { return a.w * a.h - b.w * b.h; });
    function label(p) { return POPULAR_LABELS[popularKey(p)] || ''; }
    var btn = el('button', 'dtfs-dd__btn');
    btn.type = 'button';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = '<span class="dtfs-dd__ico">' + (current ? sizeIcon(current) : '') + '</span>' +
      '<span class="dtfs-dd__txt"><b>' + (current ? esc(current.w + '" x ' + current.h + '"') : 'Select a popular size') + '</b><small>' + (current ? esc(label(current)) : 'See where each size sits on a shirt') + '</small></span>' +
      '<span class="dtfs-dd__car" aria-hidden="true">&#9662;</span>';

    var panel = el('div', 'dtfs-dd__panel');
    panel.hidden = true;
    var list = el('ul', 'dtfs-dd__list');
    list.setAttribute('role', 'listbox');
    var pv = el('aside', 'dtfs-dd__preview');
    pv.setAttribute('aria-hidden', 'true');
    function showPreview(p) {
      if (!p) { pv.innerHTML = '<p class="dtfs-dd__pvhint">Hover a size to see it on a shirt.</p>'; return; }
      pv.innerHTML = '<div class="dtfs-dd__pvico">' + sizeIcon(p, true) + '</div><b>' + esc(p.w + '" x ' + p.h + '"') + '</b><small>' + esc(label(p)) + '</small>';
    }
    showPreview(current);

    sorted.forEach(function (p) {
      var li = el('li', 'dtfs-dd__opt' + (current && popularKey(current) === popularKey(p) ? ' is-on' : ''));
      li.setAttribute('role', 'option');
      li.tabIndex = -1;
      li.innerHTML = '<span class="dtfs-dd__ico">' + sizeIcon(p) + '</span><span class="dtfs-dd__txt"><b>' + esc(p.w + '" x ' + p.h + '"') + '</b><small>' + esc(label(p)) + '</small></span>';
      function pick() { closeMenu(); onPick(p); }
      li.addEventListener('click', pick);
      li.addEventListener('mouseenter', function () { showPreview(p); });
      li.addEventListener('focus', function () { showPreview(p); });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); }
        else if (e.key === 'ArrowDown' && li.nextElementSibling) { e.preventDefault(); li.nextElementSibling.focus(); }
        else if (e.key === 'ArrowUp' && li.previousElementSibling) { e.preventDefault(); li.previousElementSibling.focus(); }
      });
      list.appendChild(li);
    });
    panel.appendChild(list);
    panel.appendChild(pv);

    btn.addEventListener('click', function () {
      var willOpen = panel.hidden;
      closeMenu();
      if (willOpen) {
        panel.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        openMenu = wrap;
        var on = list.querySelector('.is-on') || list.firstElementChild;
        if (on) { try { on.scrollIntoView({ block: 'nearest' }); } catch (e) {} }
      }
    });
    wrap.appendChild(btn);
    wrap.appendChild(panel);
    return wrap;
  }

  // ---------- rendering ----------
  function setError(msg) {
    if (!els.error) return;
    els.error.textContent = msg || '';
    els.error.hidden = !msg;
    var near = root.querySelector('[data-dtfs-error-add]');
    if (near) { near.textContent = msg || ''; near.hidden = !msg; }
  }

  function popularOptions(selectedKey) {
    var html = '<option value="smart"' + (selectedKey === 'smart' ? ' selected' : '') + '>Smart size (fits your artwork)</option>';
    html += '<optgroup label="Popular sizes">';
    parsed.popular.slice().sort(function (a, b) { return a.w * a.h - b.w * b.h; }).forEach(function (p) {
      var k = popularKey(p);
      var label = esc(p.w + '" x ' + p.h + '"') + (POPULAR_LABELS[k] ? ' - ' + esc(POPULAR_LABELS[k]) : '');
      html += '<option value="p:' + k + '"' + (selectedKey === 'p:' + k ? ' selected' : '') + '>' + label + '</option>';
    });
    html += '</optgroup><option value="custom"' + (selectedKey === 'custom' ? ' selected' : '') + '>Custom size</option>';
    return html;
  }

  function selectKey(r) {
    if (r.mode === 'popular' && r.pop) return 'p:' + popularKey(r.pop);
    return r.mode; // smart | custom
  }

  function render() {
    var pieces = totalPieces();
    var tier = tierFor(tiers, pieces);
    var has = designs.length > 0;
    if (els.empty) els.empty.hidden = has;
    if (els.steps) els.steps.hidden = !has;
    // Once there is at least one design the page switches to the "steps" screen (gallery and intro hidden).
    var wasEditing = root.classList.contains('dtfs--editing');
    root.classList.toggle('dtfs--editing', has);
    if (has && !wasEditing) {
      var grid = root.querySelector('.dtfs__grid');
      if (grid) setTimeout(function () { grid.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
    }
    if (has && !designs.some(function (x) { return x.id === activeId; })) activeId = designs[designs.length - 1].id;
    els.list.innerHTML = '';

    var subtotalCents = 0;
    var discountedCents = 0;
    var blocking = false;

    designs.forEach(function (d, di) {
      var card = el('article', 'dtfs-design' + (d.id === activeId ? ' is-current' : ''));
      card.dataset.design = d.id;
      card.addEventListener('pointerdown', function () {
        if (activeId !== d.id) { activeId = d.id; renderCustom(); els.list.querySelectorAll('.dtfs-design').forEach(function (c) { c.classList.toggle('is-current', c === card); }); }
      });

      var head = el('div', 'dtfs-design__head');
      var thumb = el('button', 'dtfs-design__thumb dtfs-bg--' + previewBg);
      thumb.type = 'button';
      thumb.setAttribute('aria-label', 'Zoom ' + d.name);
      if (d.url) thumb.innerHTML = '<img src="' + d.url + '" alt="' + esc(d.name) + '">';
      else thumb.innerHTML = '<span class="dtfs-design__file">' + esc((d.name.split('.').pop() || 'file').toUpperCase()) + '</span>';
      thumb.addEventListener('click', function () { openZoom(d); });
      head.appendChild(thumb);

      var meta = el('div', 'dtfs-design__meta');
      meta.innerHTML = '<p class="dtfs-design__name" title="' + esc(d.name) + '">' + esc(d.name) + '</p>' +
        (d.nat && d.nat.w ? '<p class="dtfs-design__dim">' + d.nat.w + ' x ' + d.nat.h + ' px</p>' : '<p class="dtfs-design__dim">Preview not available for this file type: enter your size below.</p>');
      if (d.edited) meta.insertAdjacentHTML('afterbegin', '<span class="dtfs-design__badge">Edited</span>');
      if (d.url && d.nat && !d.nat.bad && cfg.studioJs) {
        var edit = el('button', 'dtfs-link dtfs-design__edit', 'Edit design');
        edit.type = 'button';
        edit.addEventListener('click', function () { editDesign(d); });
        meta.appendChild(edit);
      }
      head.appendChild(meta);

      var del = el('button', 'dtfs-iconbtn', '&times;');
      del.type = 'button';
      del.setAttribute('aria-label', 'Remove ' + d.name);
      del.addEventListener('click', function () {
        if (d.url) URL.revokeObjectURL(d.url);
        designs = designs.filter(function (x) { return x !== d; });
        setError('');
        render();
      });
      head.appendChild(del);
      card.appendChild(head);

      if (d.big) {
        var warn = el('div', 'dtfs-warn');
        warn.innerHTML = '<strong>This file is over 20 MB.</strong> Paste a download link (Google Drive, Dropbox, WeTransfer) and we will fetch it.';
        var li = el('input', 'dtfs-input');
        li.type = 'url'; li.placeholder = 'https://'; li.value = d.link;
        li.addEventListener('input', function () { d.link = li.value.trim(); updateAddState(); });
        warn.appendChild(li);
        card.appendChild(warn);
        if (!d.link) blocking = true;
      }

      d.rows.forEach(function (r) {
        var res = rowResult(d, r);
        var isPop = r.mode === 'popular' && !!r.pop;
        var row = el('div', 'dtfs-row');
        row.dataset.row = r.id;

        // tabs: Custom size | Popular size
        var tabs = el('div', 'dtfs-sizetabs');
        tabs.setAttribute('role', 'tablist');
        tabs.innerHTML = '<button type="button" role="tab" class="dtfs-sizetab' + (isPop ? '' : ' is-on') + '" data-tab="custom"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="3 3"/><path d="M8 16l3-4 2.5 3 2-2.5L19 17"/></svg>Custom size</button>' +
          '<button type="button" role="tab" class="dtfs-sizetab' + (isPop ? ' is-on' : '') + '" data-tab="popular"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5l1.4 3 3.2.4-2.4 2.2.7 3.2-2.9-1.7-2.9 1.7.7-3.2-2.4-2.2 3.2-.4Z"/></svg>Popular size</button>';
        tabs.querySelector('[data-tab="custom"]').addEventListener('click', function () {
          if (!isPop) return;
          var sd = shownDims(d, r);
          r.mode = 'custom'; r.pop = null; r.w = round2(sd.w); r.h = round2(sd.h);
          activeRow = r; render();
        });
        tabs.querySelector('[data-tab="popular"]').addEventListener('click', function () {
          if (isPop) return;
          var cur = rowResult(d, r);
          var best = null, bestDiff = Infinity;
          parsed.popular.forEach(function (p) {
            var diff = Math.abs(p.w * p.h - cur.w * cur.h);
            if (diff < bestDiff) { best = p; bestDiff = diff; }
          });
          if (best) { r.mode = 'popular'; r.pop = best; activeRow = r; render(); }
        });
        row.appendChild(tabs);

        var help = el('p', 'dtfs-sizehelp');
        if (isPop) help.textContent = "Select a print area and we'll size it for you.";
        else {
          help.innerHTML = 'Need help with sizing? Choose a <button type="button" class="dtfs-link">Popular size</button>';
          help.querySelector('button').addEventListener('click', function () { tabs.querySelector('[data-tab="popular"]').click(); });
        }
        row.appendChild(help);

        var fields =el('div', 'dtfs-fields' + (isPop ? ' dtfs-fields--pop' : ''));

        if (isPop) {
          var pw = el('div', 'dtfs-field dtfs-field--size');
          pw.innerHTML = '<span>Size</span>';
          pw.appendChild(popularMenu(r.pop, function (p) { r.mode = 'popular'; r.pop = p; activeRow = r; render(); }));
          fields.appendChild(pw);
        } else {
          var ratio = d.nat && d.nat.w && d.nat.h ? d.nat.w / d.nat.h : r.w / r.h;
          var wf = el('label', 'dtfs-field');
          wf.innerHTML = '<span>Width (inches)</span><input class="dtfs-input" type="number" inputmode="decimal" step="0.05" min="0.5" value="' + r.w + '" data-k="w">';
          var hf = el('label', 'dtfs-field');
          hf.innerHTML = '<span>Height (inches)</span><input class="dtfs-input" type="number" inputmode="decimal" step="0.05" min="0.5" value="' + r.h + '" data-k="h">';
          var lock = el('button', 'dtfs-lock' + (r.lock ? ' is-on' : ''), '&#128274;');
          lock.type = 'button';
          lock.setAttribute('aria-label', 'Lock aspect ratio');
          lock.title = 'Lock aspect ratio';
          lock.addEventListener('click', function () { r.lock = !r.lock; render(); });
          [wf, hf].forEach(function (f) {
            var inp = f.querySelector('input');
            inp.addEventListener('change', function () {
              var v = Math.max(0.5, round2(parseFloat(inp.value) || 0.5));
              if (r.mode !== 'custom') r.mode = 'custom';
              if (inp.dataset.k === 'w') { r.w = v; if (r.lock) r.h = Math.max(0.5, round2(v / ratio)); }
              else { r.h = v; if (r.lock) r.w = Math.max(0.5, round2(v * ratio)); }
              activeRow = r;
              render();
            });
          });
          fields.appendChild(wf); fields.appendChild(lock); fields.appendChild(hf);
        }

        var qf = el('div', 'dtfs-field dtfs-field--qty');
        qf.innerHTML = '<span>Qty</span><div class="dtfs-qty"><button type="button" aria-label="Decrease quantity">&minus;</button><input type="number" min="1" max="9999" value="' + r.qty + '" aria-label="Quantity"><button type="button" aria-label="Increase quantity">+</button></div>';
        var qty = qf.querySelector('.dtfs-qty');
        var qin = qty.querySelector('input');
        qin.addEventListener('change', function () { r.qty = Math.max(1, Math.min(9999, parseInt(qin.value, 10) || 1)); activeRow = r; render(); });
        qty.children[0].addEventListener('click', function () { r.qty = Math.max(1, r.qty - 1); activeRow = r; render(); });
        qty.children[2].addEventListener('click', function () { r.qty = Math.min(9999, r.qty + 1); activeRow = r; render(); });
        fields.appendChild(qf);

        var pf = el('div', 'dtfs-field dtfs-field--price');
        var tf = el('div', 'dtfs-field dtfs-field--total');
        if (res.found && !res.tooWide) {
          var base = res.found.variant.price;
          var unit = unitAfterDiscount(base, tier.off);
          subtotalCents += base * r.qty;
          discountedCents += unit * r.qty;
          pf.innerHTML = '<span>Price</span><b>' + money(unit) + ' <small>ea</small></b>';
          tf.innerHTML = '<span>Total</span><b>' + money(unit * r.qty) + '</b>';
          r._ok = true;
        } else {
          r._ok = false; blocking = true;
          pf.className = 'dtfs-field dtfs-field--bad';
          pf.innerHTML = '<span class="dtfs-bad">' + (res.tooWide ? 'Too wide: one side must be ' + MAX_SIDE_ONE + '" or less' : 'Size not available online. <a href="/pages/contact?interest=dtf-transfers">Request a quote</a>') + '</span>';
          tf = null;
        }
        fields.appendChild(pf);
        if (tf) fields.appendChild(tf);

        var rm = el('button', 'dtfs-iconbtn dtfs-iconbtn--sm', '&times;');
        rm.type = 'button';
        rm.setAttribute('aria-label', 'Remove this size');
        rm.disabled = d.rows.length === 1;
        rm.addEventListener('click', function () { d.rows = d.rows.filter(function (x) { return x !== r; }); render(); });
        fields.appendChild(rm);

        row.appendChild(fields);
        card.appendChild(row);

        var q = dpiOf(d, r);
        if (q !== null && !(d.nat && d.nat.vector)) {
          var note = el('p', 'dtfs-dpi ' + (q >= 300 ? 'is-good' : q >= 150 ? 'is-ok' : 'is-low'));
          if (q >= 300) note.textContent = 'Great resolution at this size (' + q + ' dpi).';
          else if (q >= 150) note.textContent = 'Good resolution (' + q + ' dpi).';
          else {
            note.appendChild(document.createTextNode('Low resolution at this size (' + q + ' dpi): it may print soft. '));
            var best = maxSizeAt300(d);
            if (best) {
              note.appendChild(document.createTextNode('At 300 dpi this image prints up to ' + fmtIn(best.w) + ' x ' + fmtIn(best.h) + '. '));
              var useBtn = el('button', 'dtfs-link dtfs-dpi__btn', 'Use that size');
              useBtn.type = 'button';
              useBtn.addEventListener('click', function () {
                r.mode = 'custom'; r.pop = null; r.w = best.w; r.h = best.h; r.lock = true;
                activeRow = r; render();
              });
              note.appendChild(useBtn);
            } else {
              note.appendChild(document.createTextNode('Try a smaller size or a larger file.'));
            }
          }
          card.appendChild(note);
        }
      });

      var tools = el('div', 'dtfs-design__tools');
      var addSize = el('button', 'dtfs-link', '+ Add another size of this design');
      addSize.type = 'button';
      addSize.addEventListener('click', function () {
        var last = d.rows[d.rows.length - 1];
        var nr = newRow(d);
        nr.mode = last.mode; nr.w = last.w; nr.h = last.h; nr.pop = last.pop; nr.lock = last.lock;
        d.rows.push(nr); render();
      });
      tools.appendChild(addSize);

      var notesWrap = el('div', 'dtfs-notes');
      var nbtn = el('button', 'dtfs-link', d.notes ? 'Design notes (added)' : 'Add design notes');
      nbtn.type = 'button';
      var ta = el('textarea', 'dtfs-textarea');
      ta.rows = 2; ta.placeholder = 'Anything we should know about this design?'; ta.value = d.notes; ta.hidden = !d.notes;
      ta.addEventListener('input', function () { d.notes = ta.value; });
      nbtn.addEventListener('click', function () { ta.hidden = !ta.hidden; if (!ta.hidden) ta.focus(); });
      notesWrap.appendChild(nbtn); notesWrap.appendChild(ta);
      tools.appendChild(notesWrap);
      card.appendChild(tools);

      els.list.appendChild(card);
    });

    renderCustom();
    renderSummary(pieces, tier, subtotalCents, discountedCents, blocking);
  }

  function renderSummary(pieces, tier, subtotalCents, discountedCents, blocking) {
    var nxt = nextTier(tiers, pieces);

    // tier table
    var ref = null;
    if (activeRow) {
      designs.forEach(function (d) { d.rows.forEach(function (r) { if (r === activeRow) ref = { d: d, r: r }; }); });
    }
    if (!ref && designs[0] && designs[0].rows[0]) ref = { d: designs[0], r: designs[0].rows[0] };
    var refBase = null;
    if (ref) { var rr = rowResult(ref.d, ref.r); if (rr.found && !rr.tooWide) refBase = rr.found.variant.price; }

    var rowsHtml = '<div class="dtfs-tier dtfs-tier--head"><span>Total qty</span><span>Price</span><span>Discount</span></div>';
    var all = [{ min: 1, off: 0 }].concat(tiers);
    all.forEach(function (t, i) {
      var end = all[i + 1] ? all[i + 1].min - 1 : null;
      var label = end ? t.min + '-' + end : t.min + '+';
      var active = tier.min === t.min;
      var price = refBase != null ? money(unitAfterDiscount(refBase, t.off)) + ' <small>ea</small>' : '&mdash;';
      var note = '';
      if (active) {
        if (pieces === 0) note = 'Upload a design to see your price. We count every transfer in your order.';
        else if (nxt) note = 'Add <strong>' + (nxt.min - pieces) + '</strong> more transfer' + (nxt.min - pieces === 1 ? '' : 's') + ' of this design or your next upload to reach <strong>' + nxt.off + '% off</strong> savings.';
        else note = 'You have unlocked our best price: <strong>' + tier.off + '% off</strong>.';
        note = '<span class="dtfs-tier__note">' + note + '</span>';
      }
      rowsHtml += '<div class="dtfs-tier' + (active ? ' is-active' : '') + '"><span>' + label + '</span><span class="dtfs-tier__price">' + price + '</span><span class="dtfs-tier__off">' + (t.off ? t.off + '% off' : '') + '</span>' + note + '</div>';
    });
    els.tiers.innerHTML = rowsHtml;

    if (els.nudge) { els.nudge.textContent = ''; els.nudge.hidden = true; }

    els.pieces.textContent = pieces;
    els.sub.textContent = money(subtotalCents);
    var saved = subtotalCents - discountedCents;
    els.saveRow.hidden = saved <= 0;
    els.save.textContent = '-' + money(saved);

    var precutCents = 0;
    if (els.precutToggle && cfg.precut) {
      els.precutCost.textContent = '+' + money(cfg.precut.price) + ' each';
      if (els.precutToggle.checked) precutCents = cfg.precut.price * pieces;
    }
    els.total.textContent = money(discountedCents + precutCents);
    updateAddState(blocking, pieces);
  }

  var lastBlocking = false;
  function updateAddState(blocking, pieces) {
    if (typeof blocking === 'boolean') lastBlocking = blocking;
    var n = typeof pieces === 'number' ? pieces : totalPieces();
    var linkMissing = designs.some(function (d) { return d.big && !d.link; });
    els.add.disabled = !designs.length || n < 1 || lastBlocking || linkMissing || adding;
  }

  // ---------- zoom ----------
  function openZoom(d) {
    if (!d.url) return;
    var ov = el('div', 'dtfs-zoom dtfs-bg--' + previewBg);
    ov.innerHTML = '<button type="button" class="dtfs-zoom__x" aria-label="Close">&times;</button><img src="' + d.url + '" alt="' + esc(d.name) + '">';
    ov.addEventListener('click', function () { ov.remove(); });
    document.body.appendChild(ov);
  }

  // ---------- preview background swatches ----------
  if (els.swatches) {
    els.swatches.addEventListener('click', function (e) {
      var b = e.target.closest('[data-bg]');
      if (!b) return;
      previewBg = b.dataset.bg;
      els.swatches.querySelectorAll('[data-bg]').forEach(function (x) { x.classList.toggle('is-on', x === b); });
      render();
    });
  }

  // ---------- drop zone ----------
  els.input.addEventListener('change', function () { addFiles(els.input.files); els.input.value = ''; });
  ['dragenter', 'dragover'].forEach(function (ev) {
    els.drop.addEventListener(ev, function (e) { e.preventDefault(); els.drop.classList.add('is-over'); });
  });
  ['dragleave', 'drop'].forEach(function (ev) {
    els.drop.addEventListener(ev, function (e) { e.preventDefault(); els.drop.classList.remove('is-over'); });
  });
  els.drop.addEventListener('drop', function (e) { addFiles(e.dataTransfer && e.dataTransfer.files); });
  if (els.precutToggle) els.precutToggle.addEventListener('change', render);
  var createBtn = root.querySelector('[data-dtfs-create]');
  if (createBtn) createBtn.addEventListener('click', createDesign);

  // ---------- add to cart ----------
  var adding = false;

  function lineProps(fd, d, r, res) {
    fd.append('properties[Size]', res.w + '" x ' + res.h + '"');
    if (r.mode !== 'popular') fd.append('properties[_Print Width]', String(res.w));
    if (r.mode !== 'popular') fd.append('properties[_Print Height]', String(res.h));
    fd.append('properties[_Artwork Name]', d.name);
    fd.append('properties[_File Confirmation]', 'Customer confirmed the file is correct and final by adding to cart on ' + new Date().toISOString());
    if (d.notes) fd.append('properties[Design Notes]', d.notes);
    if (d.big && d.link) fd.append('properties[Artwork Link]', d.link);
    else fd.append('properties[_Artwork File]', d.file, d.file.name);
  }

  function post(fd) {
    return fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: fd,
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); });
  }

  els.add.addEventListener('click', function () {
    if (adding) return;
    var lines = [];
    designs.forEach(function (d) {
      d.rows.forEach(function (r) {
        var res = rowResult(d, r);
        if (res.found && !res.tooWide) lines.push({ d: d, r: r, res: res });
      });
    });
    if (!lines.length) return;
    adding = true;
    updateAddState();
    els.add.classList.add('is-loading');
    setError('');

    var pieces = totalPieces();
    var wantPrecut = els.precutToggle && els.precutToggle.checked && cfg.precut;
    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    var chain = Promise.resolve(null);
    lines.forEach(function (ln, i) {
      var isLast = i === lines.length - 1 && !wantPrecut;
      chain = chain.then(function () {
        var fd = new FormData();
        fd.append('id', ln.res.found.variant.id);
        fd.append('quantity', ln.r.qty);
        lineProps(fd, ln.d, ln.r, ln.res);
        if (isLast && cart && cart.getSectionsToRender) {
          fd.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }).join(','));
          fd.append('sections_url', window.location.pathname);
        }
        return post(fd).then(function (out) {
          if (!out.ok || out.json.status) throw new Error(out.json.description || out.json.message || 'Could not add this design to your cart.');
          return out.json;
        });
      });
    });
    if (wantPrecut) {
      chain = chain.then(function () {
        var fd = new FormData();
        fd.append('id', cfg.precut.variantId);
        fd.append('quantity', pieces);
        if (cart && cart.getSectionsToRender) {
          fd.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }).join(','));
          fd.append('sections_url', window.location.pathname);
        }
        return post(fd).then(function (out) {
          if (!out.ok || out.json.status) throw new Error(out.json.description || 'Could not add pre-cut service.');
          return out.json;
        });
      });
    }
    chain.then(function (last) {
      adding = false;
      els.add.classList.remove('is-loading');
      updateAddState();
      if (cart && cart.renderContents && last && last.sections) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(last);
      }
      else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }).catch(function (err) {
      adding = false;
      els.add.classList.remove('is-loading');
      updateAddState();
      setError(err.message || 'Something went wrong. Please try again.');
    });
  });

  render();
})();
